import React, { useState, useEffect, useCallback } from 'react';
import { X, Shield, FileText, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LegalModal = ({ isOpen, onClose, type }) => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Handle ESC key press to close modal
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      // Add ESC key listener when modal opens
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    if (isOpen && type) {
      fetchLegalPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, type]);

  const fetchLegalPage = async () => {
    setLoading(true);
    try {
      const endpoint = type === 'privacy' ? 'privacy-policy' : 'terms-of-service';
      const response = await axios.get(`${API}/legal/${endpoint}`);
      setPage(response.data.page || null);
    } catch (error) {
      console.error('Failed to fetch legal page:', error);
      // Fallback to defaults
      setPage(type === 'privacy' ? getDefaultPrivacy() : getDefaultTerms());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPrivacy = () => ({
    title: 'Privacy Policy',
    content: 'Loading privacy policy...'
  });

  const getDefaultTerms = () => ({
    title: 'Terms of Service',
    content: 'Loading terms of service...'
  });

  const getIcon = () => {
    return type === 'privacy' ? Shield : FileText;
  };

  const Icon = getIcon();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="legal-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="legal-modal-backdrop"
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[85vh] mx-4 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-900 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-amber-400" />
            </div>
            <h2 className="text-xl font-semibold text-white" data-testid="legal-modal-title">
              {page?.title || (type === 'privacy' ? 'Privacy Policy' : 'Terms of Service')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
            aria-label="Close"
            data-testid="legal-modal-close-btn"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(85vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
            </div>
          ) : (
            <div 
              className="space-y-4"
              data-testid="legal-modal-content"
            >
              {page?.content?.split('\n').map((line, index) => {
                // Handle markdown-like formatting
                if (line.startsWith('## ')) {
                  return (
                    <h2 key={index} className="text-2xl font-semibold text-white mt-8 mb-4 pb-2 border-b border-slate-700">
                      {line.replace('## ', '')}
                    </h2>
                  );
                }
                if (line.startsWith('### ')) {
                  return (
                    <h3 key={index} className="text-lg font-semibold text-amber-400 mt-6 mb-3">
                      {line.replace('### ', '')}
                    </h3>
                  );
                }
                if (line.startsWith('**') && line.endsWith('**')) {
                  return (
                    <p key={index} className="text-white font-semibold">
                      {line.replace(/\*\*/g, '')}
                    </p>
                  );
                }
                if (line.startsWith('- ')) {
                  return (
                    <li key={index} className="text-slate-200 ml-4 list-disc">
                      {line.replace('- ', '')}
                    </li>
                  );
                }
                if (line.trim() === '') {
                  return <div key={index} className="h-2" />;
                }
                // Handle inline bold
                const parts = line.split(/(\*\*[^*]+\*\*)/g);
                return (
                  <p key={index} className="text-slate-200 leading-relaxed">
                    {parts.map((part, i) => {
                      if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={i} className="text-white font-semibold">{part.replace(/\*\*/g, '')}</strong>;
                      }
                      return part;
                    })}
                  </p>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900 border-t border-slate-700 px-6 py-4">
          <p className="text-sm text-slate-400 text-center">
            Questions?{' '}
            <a 
              href="mailto:help@willkart.ai"
              className="text-amber-400 hover:text-amber-300 font-medium"
            >
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalModal;
